/* eslint-disable max-lines */
import React, { useState, useEffect, useCallback } from 'react'; // Added useCallback
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner } from 'reactstrap';
import moment from 'moment';
import { fillIT2104Form } from '../../../../services/IT2104PDFService';
import { FormIT2104Data } from '../../NewHire/FormIT2104Modal';

interface IT2104PreviewModalProps {
  isOpen: boolean;
  toggleModal: () => void;
  uploadedPdf?: string;
  formData: FormIT2104Data | null;
}

const IT2104PreviewModal: React.FC<IT2104PreviewModalProps> = ({ isOpen, toggleModal, uploadedPdf, formData }) => {
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const staticPdfFileUrl =
    uploadedPdf || 'https://arb-systems-s3-public.s3.us-east-2.amazonaws.com/public/it-2104-form.pdf';

  const generatePdf = useCallback(async () => {
    if (!formData) return;
    const dataToFill: any = formData || {};

    if (!dataToFill) {
      setError('Form data is not available to generate the PDF.');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl('');
    }

    try {
      const filledPdfBytes = await fillIT2104Form(staticPdfFileUrl, dataToFill);
      if (filledPdfBytes) {
        const blob = new Blob([filledPdfBytes], { type: 'application/pdf' });
        const docUrl = URL.createObjectURL(blob);
        setPdfUrl(docUrl);
      } else {
        setError('Failed to generate the filled PDF bytes.');
      }
    } catch (e) {
      console.error('Error generating PDF preview:', e);
      setError(e instanceof Error ? e.message : 'An unknown error occurred while generating PDF.');
    } finally {
      setIsLoading(false);
    }
  }, [pdfUrl, formData]);

  useEffect(() => {
    if (isOpen) {
      generatePdf();
    } else {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl('');
      }
      setError(null);
      setIsLoading(false);
    }

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [isOpen]);

  const handleDownload = () => {
    if (!pdfUrl || !formData) {
      console.error('PDF URL or Form Data not available for download.');
      return;
    }
    const link = document.createElement('a');
    link.href = pdfUrl;
    const lastNameForFile = formData?.lastName?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'employee';
    const yearForFile = formData?.year || moment().format('YYYY');
    link.download = `IT-2104_${lastNameForFile}_${yearForFile}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Modal className="modal-dialog-centered" size="xl" isOpen={isOpen} toggle={toggleModal} backdrop="static">
      <ModalHeader className="pb-1" toggle={toggleModal}>
        IT-2104 Form Preview
      </ModalHeader>
      <ModalBody className="pt-0">
        <Row className="mt-2">
          <Col md="12">
            <div className="d-flex justify-content-end mb-2">
              <Button color="primary" onClick={handleDownload} size="sm" disabled={!pdfUrl || isLoading || !!error}>
                Download PDF
              </Button>
            </div>
          </Col>
          <Col
            md="12"
            style={{
              minHeight: '500px',
              maxHeight: '70vh',
              overflowY: 'auto',
              border: '1px solid #dee2e6',
              position: 'relative',
            }}
          >
            {isLoading && (
              <div
                className="text-center p-5"
                style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}
              >
                <Spinner style={{ width: '3rem', height: '3rem' }} />
                <p className="mt-2">Generating PDF...</p>
              </div>
            )}
            {error && !isLoading && <div className="text-center text-danger p-3">Error generating PDF: {error}</div>}
            {!isLoading && !error && pdfUrl && (
              <iframe
                src={pdfUrl}
                style={{ width: '100%', height: 'calc(70vh - 20px)', border: 'none' }}
                title="IT-2104 Form Preview"
              />
            )}
            {!isLoading && !error && !pdfUrl && isOpen && (
              <div className="text-center p-3">Preparing PDF preview... If this persists, there might be an issue.</div>
            )}
          </Col>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggleModal}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default IT2104PreviewModal;
